/* eslint-disable import/extensions */
import DesktopSkeleton from '@components/pages/common/DesktopSkeleton';
import SuspenseWithMinimumLoading from '@components/SuspenseWithMinimumLoading';
import { QueryStatus } from '@hooks/useVisiblePromotionProducts';
import { toPromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { ICoupon } from '@wconcept/wconcept-common/models/event/custom';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import {
  PromotionContent,
  PromotionHtmlWideModule,
  PromotionImageVideoModule,
  PromotionInfo,
  PromotionKeyVisualModule,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import {
  AdvertisementModule,
  BannerModule,
  BasicNavigationModule,
  BrandModule,
  CommonNavigationModule,
  CouponModule,
  FcfsCouponModule,
  FirstPurchaseModule,
  HighlightPickModule,
  HtmlSection,
  MainBannerModule,
  MainVisualModule,
  PaymentBenefitModule,
  ProductModule,
  QuickBannerModule,
  RankingModuleContainer,
  RelatedContentModule,
  ShortformModule,
  SpecialPriceModule,
  WLiveModule,
  EntryModule,
} from '@wconcept/wconcept-display/components/pages/promotion/PromotionModules';
import SubSkeleton02 from '@wconcept/wconcept-ui-renewal/src/components/molecules/_skeleton/SubSkeleton02';
import dynamic from 'next/dynamic';
import React from 'react';
import ModuleAnchorWrapper from '../components/ModuleAnchorWrapper';
import BrandCollectionModule from '../PromotionModules/BrandCollectionModule';
import { isInDisplayTimeRange } from './displayTimeChecker';
import { ModuleErrorBoundary } from './ErrorBoundary';
import {
  isBasicNavigationModule,
  isBenefitsModule,
  isBrandModule,
  isCommonNavigationModule,
  isContentModule,
  isCouponModule,
  isFcfsCouponModule,
  isFirstPurchaseModule,
  isHighlightPickModule,
  isHtmlModule,
  isKeyVisualModule,
  isLiveModule,
  isMainBannerModule,
  isQuickBannerModule,
  isMediaModule,
  isProductModule,
  isRankingModule,
  isSpecialPriceModule,
  isStyleClipsModule,
  isBrandCollectionModule,
  isEntryModule,
} from './typeGuards';
import { renderTopBuyerModule } from './topBuyerRenderer';

export { renderTopBuyerModule };

const ProductListMobileModule = dynamic(
  () => import('../PromotionModules/ProductListModule'),
  {
    ssr: false,
    loading: () => <SubSkeleton02 />,
  }
);
const ProductListDesktopModule = dynamic(
  () => import('../PromotionModules/ProductListModule'),
  {
    ssr: false,
    loading: () => <DesktopSkeleton />,
  }
);

// const ProductListModule = React.lazy(
//   () => import('../PromotionModules/ProductListModule')
// );
// 모듈 렌더러 인터페이스
export interface ModuleRendererProps {
  content: PromotionContent;
  promotionContents?: PromotionContent[];
  promotion?: PromotionInfo;
  isMobile: boolean;
  promotionTitle?: string;
  nonce?: string;
  key?: string | number;
  sectionNumbers?: number;
  registerModule?: (contentId: string, element: HTMLElement | null) => void;
  isModuleVisible?: (contentId: string) => boolean;
  queryStatusMap?: Map<string, QueryStatus>;
  directQueryStatusMap?: Map<
    string,
    {
      isLoading: boolean;
      isError: boolean;
      isFetching: boolean;
      isSuccess: boolean;
      error: unknown;
      data: Product[] | undefined;
    }
  >;
  productListTotalCount?: number;
  isPaused?: boolean;
  setIsPaused?: (value: boolean) => void;
  gnbType?: boolean;
  isLogin?: boolean;
}

// 모듈 렌더러 타입
export type ModuleRenderer = (props: ModuleRendererProps) => React.ReactNode;

/**
 * 모듈별 렌더링 로직을 캡슐화한 유틸리티 함수들
 */

/**
 * HTML_WIDE 모듈 렌더링
 */
export const renderHtmlModule = (
  content: PromotionContent,
  isMobile: boolean,
  nonce?: string,
  registerModule?: (contentId: string, element: HTMLElement | null) => void,
  handleHtmlComplete?: () => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const htmlModule = content.modules?.filter(isHtmlModule);
  const filteredHtmlModule = htmlModule?.filter(
    module =>
      isInDisplayTimeRange(module.displayStartDate, module.displayEndDate) &&
      module.displayStatus === 'USE'
  );

  const getPayload = (module: PromotionHtmlWideModule) => {
    let css = module.styleContent;
    let script = module.scriptContent;
    let html = module.templateContent;
    if (module.htmlDisplayType === 'ADAPTIVE') {
      css = isMobile ? module.moStyleContent : module.pcStyleContent;
    }
    if (module.htmlDisplayType === 'ADAPTIVE') {
      script = isMobile ? module.moScriptContent : module.pcScriptContent;
    }
    if (module.htmlDisplayType === 'ADAPTIVE') {
      html = isMobile ? module.moTemplateContent : module.pcTemplateContent;
    }
    return { css, script, html };
  };

  const firstHtmlModule = filteredHtmlModule?.[0];
  if (!firstHtmlModule) return null;

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-html-section"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <HtmlSection
          payload={{
            css: getPayload(firstHtmlModule).css || '',
            script: getPayload(firstHtmlModule).script || '',
            html: getPayload(firstHtmlModule).html || '',
          }}
          nonce={nonce}
          order={content.displayOrder}
          key={content.contentId}
          handleHtmlComplete={handleHtmlComplete}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * IMAGE_VIDEO 모듈 렌더링
 */
export const renderImageVideoModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void,
  promotion?: PromotionInfo
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const mediaModule = content.modules?.filter(isMediaModule);
  const gaInfo = {
    cd: promotion?.promotionId,
    nm: promotion?.title,
    page: 'promotion',
    area: 'promotion_imagevideo_banner',
  };

  const filteredMediaModule = mediaModule?.filter(
    module =>
      isInDisplayTimeRange(module.displayStartDate, module.displayEndDate) &&
      module.displayStatus === 'USE'
  );

  if (!filteredMediaModule) return null;

  const renderImgSrc = (module: PromotionImageVideoModule) => {
    if (module.mediaType === 'IMAGE') {
      return isMobile ? module.moImageUrl : module.pcImageUrl;
    }
    return '';
  };

  const renderVideoSrc = (module: PromotionImageVideoModule) => {
    if (module.mediaType === 'VIDEO') {
      return isMobile
        ? module.moVodInfo?.playBackUrl
        : module.pcVodInfo?.playBackUrl;
    }
    return '';
  };

  const renderVideoPoster = (module: PromotionImageVideoModule) => {
    if (module.mediaType === 'VIDEO') {
      return isMobile
        ? module.moVodInfo?.thumbnailUrl
        : module.pcVodInfo?.thumbnailUrl;
    }
    return '';
  };

  const getVideoToken = (module: PromotionImageVideoModule) => {
    if (module.mediaType === 'VIDEO' && isMobile) {
      return module.moVodInfo?.token
        ? { playAuthToken: module.moVodInfo.token }
        : undefined;
    }
    return module.pcVodInfo?.token
      ? { playAuthToken: module.pcVodInfo.token }
      : undefined;
  };

  const getVideoVid = (module: PromotionImageVideoModule) => {
    if (module.mediaType === 'VIDEO' && isMobile) {
      return module.moVodInfo?.id;
    }
    return module.pcVodInfo?.id;
  };

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-banner"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        {filteredMediaModule.map(module => (
          <BannerModule
            vid={module.mediaType === 'VIDEO' ? getVideoVid(module) : ''}
            videoSrc={renderVideoSrc(module)}
            videoPoster={renderVideoPoster(module)}
            token={getVideoToken(module)}
            imgSrc={renderImgSrc(module)}
            key={module.moduleId}
            order={content.displayOrder}
            landingUrl={module.landingUrl || ''}
            gaInfo={{ ...gaInfo, text: module.title }}
          />
        ))}
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * KEY_VISUAL 모듈 렌더링
 */
export const renderKeyVisualModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const mediaModule = content.modules?.filter(isKeyVisualModule);
  const filteredMediaModule = mediaModule?.filter(module =>
    isInDisplayTimeRange(module.displayStartDate, module.displayEndDate)
  );

  const getVideoSrc = (module: PromotionKeyVisualModule) => {
    if (module.mediaType === 'VIDEO') {
      return isMobile ? module.moVodInfo?.playBackUrl : undefined;
    }
    return '';
  };
  const getVideoPoster = (module: PromotionKeyVisualModule) => {
    if (module.mediaType === 'VIDEO') {
      return isMobile
        ? module.moVodInfo?.thumbnailUrl
        : module.pcVodInfo?.thumbnailUrl;
    }
    return '';
  };
  const getImgSrc = (module: PromotionKeyVisualModule) => {
    if (module.mediaType === 'IMAGE') {
      return isMobile ? module.moImageUrl : module.pcImageUrl;
    }
    return '';
  };
  const getVid = (module: PromotionKeyVisualModule) => {
    if (module.mediaType === 'VIDEO') {
      return isMobile ? module.moVodInfo?.id : module.pcVodInfo?.id;
    }
    return '';
  };
  const getToken = (module: PromotionKeyVisualModule) => {
    if (module.mediaType === 'VIDEO') {
      return isMobile ? module.moVodInfo?.token : module.pcVodInfo?.token;
    }
    return '';
  };
  const getSizeType = (module: PromotionKeyVisualModule) => {
    const layoutType = isMobile ? module.moLayoutType : module.pcLayoutType;

    return layoutType === 'COMPACT' ? 'small' : 'default';
  };

  if (!filteredMediaModule || !filteredMediaModule?.length) return null;

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-main-visual"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        {filteredMediaModule.map(module => (
          <MainVisualModule
            vid={getVid(module)}
            videoSrc={getVideoSrc(module)}
            videoPoster={getVideoPoster(module)}
            imgSrc={getImgSrc(module)}
            token={getToken(module)}
            sizeType={getSizeType(module)}
            title={module.mainTitle || ''}
            subTitle={module.subTitle || ''}
            key={content.contentId}
            order={content.displayOrder}
            promotionId={content.promotionId.toString()}
            promotionName={content.mainTitle || ''}
          />
        ))}
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * SPECIAL_PRICE 모듈 렌더링
 */
export const renderSpecialPriceModule = ({
  content,
  isMobile,
  registerModule,
  isModuleVisible,
  promotion,
}: ModuleRendererProps): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const specialPriceModule = content.modules?.filter(isSpecialPriceModule);
  const filteredSpecialPriceModule = specialPriceModule?.filter(module =>
    isInDisplayTimeRange(module.displayStartDate, module.displayEndDate)
  );
  if (!filteredSpecialPriceModule || !filteredSpecialPriceModule?.length) {
    return null;
  }

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'full',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-special-price-product"
        id={`section${content.displayOrder}`}
        registerModule={registerModule}
        key={content.contentId}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        {filteredSpecialPriceModule.map(module => (
          <SpecialPriceModule
            bgType={content.colorType?.toLowerCase() || ''}
            visualTokens={visualTokens}
            isDesktop={!isMobile}
            subTitle={content.subTitle || ''}
            schedules={module.schedules}
            periodType={module.periodType}
            title={content.mainTitle}
            promotionId={content.promotionId}
            key={module.moduleId}
            contentId={content.contentId.toString()}
            promotionTitle={promotion?.title || ''}
            isVisible={isModuleVisible?.(content.contentId.toString())}
          />
        ))}
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * FIRST_PURCHASE 모듈 렌더링
 */
export const renderFirstPurchaseModule = ({
  content,
  isMobile,
  registerModule,
  isModuleVisible,
  promotion,
}: ModuleRendererProps): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const firstPurchaseModules = content.modules?.filter(isFirstPurchaseModule);
  const filteredFirstPurchaseModules = firstPurchaseModules?.filter(module =>
    isInDisplayTimeRange(module.displayStartDate, module.displayEndDate)
  );
  if (!filteredFirstPurchaseModules?.length) {
    return null;
  }

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'full',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-special-price-product"
        id={`section${content.displayOrder}`}
        registerModule={registerModule}
        key={content.contentId}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        {filteredFirstPurchaseModules.map(module => (
          <FirstPurchaseModule
            bgType={content.colorType?.toLowerCase() || ''}
            visualTokens={visualTokens}
            isDesktop={!isMobile}
            subTitle={content.subTitle || ''}
            schedules={module.schedules ?? []}
            periodType={module.periodType}
            title={content.mainTitle}
            promotionId={content.promotionId}
            key={module.moduleId}
            contentId={content.contentId.toString()}
            promotionTitle={promotion?.title || ''}
            isVisible={isModuleVisible?.(content.contentId.toString())}
            titleBannerPcImageUrl={module.titleBannerPcImageUrl}
            titleBannerMoImageUrl={module.titleBannerMoImageUrl}
            moduleSubText={module.moduleSubText}
          />
        ))}
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * RANKING 모듈 렌더링
 */
export const renderRankingModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const rankingModule = content.modules?.filter(isRankingModule);
  const filteredRankingModule = rankingModule?.filter(module =>
    isInDisplayTimeRange(module.displayStartDate, module.displayEndDate)
  );
  if (!filteredRankingModule || !filteredRankingModule?.length) return null;

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'full',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-ranking-product"
        id={`section${content.displayOrder}`}
        registerModule={registerModule}
        key={content.contentId}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        {filteredRankingModule.map(module => (
          <RankingModuleContainer
            bgType={content.colorType?.toLowerCase() || ''}
            visualTokens={visualTokens}
            isDesktop={!isMobile}
            categoryList={module.categories}
            subTitle={content.subTitle}
            updateTime={module.displayEndDate}
            order={content.displayOrder}
            title={content.mainTitle}
            promotionId={content.promotionId}
            key={module.moduleId}
            registerModule={registerModule}
            contentId={content.contentId.toString()}
          />
        ))}
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * PAYMENT_BENEFIT 모듈 렌더링
 */
export const renderPaymentBenefitModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const benefitsModule = content.modules?.filter(isBenefitsModule);
  const filteredBenefitsModule = benefitsModule?.filter(module =>
    isInDisplayTimeRange(module.displayStartDate, module.displayEndDate)
  );
  if (!filteredBenefitsModule || !filteredBenefitsModule?.length) return null;

  const filteredNotices = content.notices?.filter(
    notice =>
      isInDisplayTimeRange(notice.displayStartDate, notice.displayEndDate) &&
      notice.displayStatus === 'USE'
  );

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'full',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-payment-benefit"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <PaymentBenefitModule
          bgType={content.colorType?.toLowerCase() || ''}
          visualTokens={visualTokens}
          isDesktop={!isMobile}
          paymentBenefitData={filteredBenefitsModule}
          noticeData={filteredNotices || []}
          mainTitle={content.mainTitle || ''}
          subTitle={content.subTitle || ''}
          key={content.contentId}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * SHORT_FORM 모듈 렌더링
 */
export const renderShortFormModule = ({
  content,
  isMobile,
  registerModule,
  queryStatusMap,
  directQueryStatusMap,
  isPaused,
  setIsPaused,
}: ModuleRendererProps): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const styleClipsModule = content.modules?.filter(isStyleClipsModule);
  const filteredStyleClipsModule = styleClipsModule?.filter(module =>
    isInDisplayTimeRange(module.displayStartDate, module.displayEndDate)
  );

  if (!filteredStyleClipsModule) return null;

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'twoField',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-shortform"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        {filteredStyleClipsModule.map(module => (
          <ShortformModule
            bgType={content.colorType?.toLowerCase() || ''}
            visualTokens={visualTokens}
            isDesktop={!isMobile}
            subTitle={content.subTitle || ''}
            title={content.mainTitle || ''}
            isPaused={isPaused}
            styleClips={module.styleClips}
            setIsPaused={setIsPaused}
            key={module.moduleId}
            queryStatusMap={queryStatusMap}
            directQueryStatusMap={directQueryStatusMap}
            landingUrl={module.landingUrl || ''}
            contentId={content.contentId.toString()}
            promotionId={content.promotionId}
            promotionTitle={content.mainTitle || ''}
          />
        ))}
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * CONTENT 모듈 렌더링
 */
export const renderContentModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void,
  promotionTitle?: string
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const contentModule = content.modules?.filter(isContentModule);

  const filteredContentModule = contentModule
    ?.filter(
      module =>
        isInDisplayTimeRange(module.displayStartDate, module.displayEndDate) &&
        module.displayStatus === 'USE'
    )
    .slice(0, 10);

  if (!filteredContentModule) return null;

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'twoField',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-related-contents"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <RelatedContentModule
          bgType={content.colorType?.toLowerCase() || ''}
          visualTokens={visualTokens}
          isDesktop={!isMobile}
          title={content.mainTitle || ''}
          subTitle={content.subTitle || ''}
          relatedContentsData={filteredContentModule}
          promotionId={content.promotionId}
          key={content.contentId}
          promotionTitle={promotionTitle || ''}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * BASIC_NAVIGATION 모듈 렌더링
 */
export const renderBasicNavigationModule = (
  content: PromotionContent,
  isMobile: boolean,
  promotionContents: readonly PromotionContent[],
  registerModule?: (contentId: string, element: HTMLElement | null) => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const basicNavigationModule = content.modules?.filter(
    isBasicNavigationModule
  );
  const filteredBasicNavigationModule = basicNavigationModule?.filter(module =>
    isInDisplayTimeRange(module.displayStartDate, module.displayEndDate)
  );

  if (
    !filteredBasicNavigationModule ||
    !filteredBasicNavigationModule?.length
  ) {
    return null;
  }
  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'full',
  });
  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-navi corner-navi1"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        {filteredBasicNavigationModule.map(module => (
          <BasicNavigationModule
            bgType={content.colorType?.toLowerCase() || ''}
            visualTokens={visualTokens}
            isDesktop={!isMobile}
            naviData={module.tabs || []}
            promotionContents={promotionContents}
            key={module.moduleId}
            activeColor={module.mainColor || ''}
          />
        ))}
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * COMMON_NAVIGATION 모듈 렌더링
 */
export const renderCommonNavigationModule = (
  content: PromotionContent,
  isMobile: boolean,
  promotionTitle: string,
  registerModule?: (contentId: string, element: HTMLElement | null) => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const commonNavigationModule = content.modules?.filter(
    isCommonNavigationModule
  );
  const filteredCommonNavigationModule = commonNavigationModule?.filter(
    module =>
      isInDisplayTimeRange(module.displayStartDate, module.displayEndDate)
  );
  if (
    !filteredCommonNavigationModule ||
    !filteredCommonNavigationModule?.length
  )
    return null;

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-navi corner-navi2"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        {filteredCommonNavigationModule.map(module => (
          <CommonNavigationModule
            isDesktop={!isMobile}
            navigationId={module.navigationId}
            key={content.contentId}
            promotionTitle={promotionTitle}
            promotionId={content.promotionId}
          />
        ))}
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * COUPON 모듈 렌더링
 */
export const renderCouponModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const couponModule = content.modules?.filter(isCouponModule);
  const getCoupontFilter = (v: ICoupon[]) =>
    v.filter(
      item =>
        isInDisplayTimeRange(item.issueStartDate, item.issueEndDate) &&
        !item.soldOut &&
        item.ableDown
    );

  // 서버 시간 기준으로 현재 시간이 displayStartDate와 displayEndDate 범위 내에 있는 쿠폰만 필터링
  const filteredCouponData = couponModule
    ?.filter(coupon =>
      isInDisplayTimeRange(coupon.displayStartDate, coupon.displayEndDate)
    )
    .map(coupon => ({
      ...coupon,
      couponItems: getCoupontFilter(coupon.couponItems),
    }))
    .filter(coupon => coupon.couponItems.length > 0);
  if (!filteredCouponData?.length) return null;

  const filteredNotices = content.notices?.filter(
    notice =>
      isInDisplayTimeRange(notice.displayStartDate, notice.displayEndDate) &&
      notice.displayStatus === 'USE'
  );
  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'twoField',
  });
  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-mega-coupon"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <CouponModule
          key={content.contentId}
          bgType={content.colorType?.toLowerCase() || ''}
          visualTokens={visualTokens}
          couponData={filteredCouponData.slice(
            0,
            content.moduleDisplayType === 'PACK' ? 6 : 3
          )}
          subTitle={content.subTitle || ''}
          title={content.mainTitle || ''}
          order={content.displayOrder}
          noticeData={filteredNotices}
          displayType={content.moduleDisplayType}
          isDesktop={!isMobile}
          registerModule={registerModule}
          contentId={content.contentId.toString()}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * ADVERTISEMENT 모듈 렌더링
 */
export const renderAdvertisementModule = (
  content: PromotionContent,
  isMobile: boolean,
  promotion?: PromotionInfo,
  registerModule?: (contentId: string, element: HTMLElement | null) => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const advertisementModule = content.moduleType === 'ADVERTISEMENT';
  if (!advertisementModule) return null;

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'twoField',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-advertisement"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <AdvertisementModule
          key={content.contentId}
          order={content.displayOrder}
          registerModule={registerModule}
          contentId={content.contentId.toString()}
          isMobile={!!isMobile}
          promotionId={content.promotionId}
          promotion={promotion}
          title={content.mainTitle}
          bgType={content.colorType?.toLowerCase() || ''}
          visualTokens={visualTokens}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * W_LIVE 모듈 렌더링
 */
export const renderWLiveModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const wLiveModule = content.modules?.filter(isLiveModule);
  const filteredWLiveModule = wLiveModule?.filter(
    module =>
      isInDisplayTimeRange(module.displayStartDate, module.displayEndDate) &&
      module.displayStatus === 'USE'
  );
  if (!filteredWLiveModule || !filteredWLiveModule?.length) return null;

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'full',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-w-live"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <WLiveModule
          title={content.mainTitle || ''}
          key={content.contentId}
          bgType={content.colorType?.toLowerCase() || ''}
          visualTokens={visualTokens}
          isDesktop={!isMobile}
          subTitle={content.subTitle || ''}
          promotionId={content.promotionId}
          promotionTitle={content.mainTitle || ''}
          wLiveData={filteredWLiveModule}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * BRAND 모듈 렌더링
 */
export const renderBrandModule = (
  content: PromotionContent,
  isMobile: boolean,
  promotionTitle?: string,
  registerModule?: (contentId: string, element: HTMLElement | null) => void,
  isModuleVisible?: (contentId: string) => boolean
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const brandModules = content.modules?.filter(isBrandModule);
  const filteredBrandModules = brandModules?.filter(module =>
    isInDisplayTimeRange(module.displayStartDate, module.displayEndDate)
  );

  if (!filteredBrandModules || !filteredBrandModules?.length) return null;

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'full',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-brand"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        {filteredBrandModules.map(module => (
          <BrandModule
            brandModules={module}
            key={`${content.contentId}-${module.moduleId}`}
            bgType={content.colorType?.toLowerCase() || ''}
            visualTokens={visualTokens}
            isDesktop={!isMobile}
            promotionId={content.promotionId}
            promotionTitle={promotionTitle || ''}
            subTitle={content.subTitle || ''}
            title={content.mainTitle || ''}
            contentId={content.contentId.toString()}
            isVisible={isModuleVisible?.(content.contentId.toString())}
          />
        ))}
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * PRODUCT 모듈 렌더링
 */
export const renderProductModule = (
  content: PromotionContent,
  isMobile: boolean,
  promotionTitle?: string,
  registerModule?: (contentId: string, element: HTMLElement | null) => void,
  isModuleVisible?: (contentId: string) => boolean
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const productModule = content.modules?.filter(isProductModule);
  const filteredProductModule = productModule?.filter(module =>
    isInDisplayTimeRange(module.displayStartDate, module.displayEndDate)
  );
  if (!filteredProductModule || !filteredProductModule?.length) return null;

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'twoField',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-product"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        {filteredProductModule.map(module => (
          <ProductModule
            promotionId={content.promotionId}
            key={module.moduleId}
            bgType={content.colorType?.toLowerCase() || ''}
            visualTokens={visualTokens}
            isDesktop={!isMobile}
            title={content.mainTitle || ''}
            subTitle={content.subTitle || ''}
            module={module}
            contentId={content.contentId}
            isVisible={isModuleVisible?.(content.contentId.toString())}
          />
        ))}
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * PRODUCT_LIST 모듈 렌더링
 */
export const renderProductListModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void,
  productListTotalCount?: number
) => {
  if (content.displayStatus !== 'USE') return null;

  if (content.moduleType !== 'PRODUCT_LIST') return null;

  if (productListTotalCount === 0) {
    return null;
  }

  const productListModuleId = content.modules?.[0]
    ? (content.modules[0] as { moduleId: number }).moduleId
    : undefined;

  return (
    <ModuleAnchorWrapper
      order={content.displayOrder}
      className="corner-lp-wrap"
      key={content.contentId}
      id={`section${content.displayOrder}`}
      registerModule={registerModule}
      contentId={content.contentId.toString()}
      isDesktop={!isMobile}
    >
      {isMobile ? (
        <SuspenseWithMinimumLoading fallback={<SubSkeleton02 />}>
          <ProductListMobileModule
            id={content.promotionId.toString()}
            key={content.contentId}
            bgType={content.colorType?.toLowerCase() || ''}
            isMobile={isMobile}
            contentId={content.contentId}
            moduleId={productListModuleId}
          />
        </SuspenseWithMinimumLoading>
      ) : (
        <SuspenseWithMinimumLoading fallback={<DesktopSkeleton />}>
          <ProductListDesktopModule
            id={content.promotionId.toString()}
            key={content.contentId}
            bgType={content.colorType?.toLowerCase() || ''}
            isMobile={isMobile}
            contentId={content.contentId}
            moduleId={productListModuleId}
          />
        </SuspenseWithMinimumLoading>
      )}
    </ModuleAnchorWrapper>
  );
};

/**
 * FCFS_COUPON 모듈 렌더링.
 */
export const renderFcfsCouponModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void,
  promotionTitle?: string,
  promotion?: PromotionInfo | null
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const fcfsCouponModule = content.modules?.find(isFcfsCouponModule);
  if (!fcfsCouponModule) return null;

  const filteredNotices = content.notices?.filter(
    notice =>
      isInDisplayTimeRange(notice.displayStartDate, notice.displayEndDate) &&
      notice.displayStatus === 'USE'
  );

  const visualTokens = toPromotionModuleVisualTokens(content, {
    /** CouponItem2 커스텀은 point·강조 HEX가 필요해 full 마스크 사용 */
    mask: 'full',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-mega-coupon"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <FcfsCouponModule
          key={content.contentId}
          promotionId={content.promotionId}
          moduleId={fcfsCouponModule.moduleId}
          bgType={content.colorType?.toLowerCase() || ''}
          visualTokens={visualTokens}
          title={content.mainTitle || ''}
          subTitle={content.subTitle || ''}
          module={fcfsCouponModule}
          noticeData={filteredNotices}
          promotionTitle={promotionTitle || ''}
          promotionDisplayBounds={
            promotion?.displayStartDate && promotion?.displayEndDate
              ? {
                  displayStartDate: promotion.displayStartDate,
                  displayEndDate: promotion.displayEndDate,
                }
              : null
          }
          isDesktop={!isMobile}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * HIGHLIGHT_PICK 모듈 렌더링.
 * 슬롯 0건이어도 `ModuleErrorBoundary` + `ModuleAnchorWrapper`는 유지한다(§2.6).
 * 카드 순서·상한·유효 상품 필터는 `HighlightPickModule`이 담당한다.
 */
export const renderHighlightPickModule = ({
  content,
  isMobile,
  registerModule,
  isModuleVisible,
  promotion,
}: ModuleRendererProps): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const highlightPickModules = content.modules?.filter(isHighlightPickModule);
  const filteredModules =
    highlightPickModules?.filter(
      module =>
        isInDisplayTimeRange(module.displayStartDate, module.displayEndDate) &&
        module.displayStatus === 'USE'
    ) ?? [];

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'twoField',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-highlight-pick"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <HighlightPickModule
          bgType={content.colorType?.toLowerCase() || ''}
          visualTokens={visualTokens}
          isDesktop={!isMobile}
          title={content.mainTitle}
          subTitle={content.subTitle}
          modules={filteredModules}
          contentId={content.contentId}
          promotionId={content.promotionId}
          promotionTitle={promotion?.title}
          isVisible={isModuleVisible?.(content.contentId.toString())}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * 응모 이벤트 모듈 렌더러.
 */
export const renderEntryModule = ({
  content,
  isMobile,
  promotionTitle,
  registerModule,
}: ModuleRendererProps): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  // 응모 기간이 지나도 모듈은 노출 유지 — 기간 체크는 클릭 시점에 EntryModule에서 처리
  const entryModule = content.modules?.find(isEntryModule);
  if (entryModule?.displayStatus !== 'USE') return null;

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'full',
  });

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-entry"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <EntryModule
          bgType={content.colorType?.toLowerCase() || ''}
          visualTokens={visualTokens}
          isDesktop={!isMobile}
          module={entryModule}
          promotionId={content.promotionId}
          promotionTitle={promotionTitle || ''}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * BRAND_COLLECTION 모듈 렌더링
 */
export const renderBrandCollectionModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void,
  gnbType?: boolean
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const brandCollectionModules = content.modules?.filter(
    isBrandCollectionModule
  );
  const filteredModules = brandCollectionModules?.filter(
    module =>
      isInDisplayTimeRange(module.displayStartDate, module.displayEndDate) &&
      module.displayStatus === 'USE'
  );

  const visualTokens = toPromotionModuleVisualTokens(content, {
    mask: 'full',
  });

  if (!filteredModules || !filteredModules.length) return null;
  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        className="corner-collection-section"
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <BrandCollectionModule
          content={content}
          isMobile={isMobile}
          bgType={content.colorType?.toLowerCase() || ''}
          visualTokens={visualTokens}
          gnbType={gnbType}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

/**
 * 모듈 타입별 렌더러 매핑
 */
export const promotionModuleRenderers: Record<string, ModuleRenderer> = {
  HTML_WIDE: ({ content, nonce, isMobile }) =>
    renderHtmlModule(content, isMobile, nonce),
  IMAGE_VIDEO: ({ content, isMobile, registerModule }) =>
    renderImageVideoModule(content, isMobile, registerModule),
  KEY_VISUAL: ({ content, isMobile, registerModule }) =>
    renderKeyVisualModule(content, isMobile, registerModule),
  SPECIAL_PRICE: ({
    content,
    isMobile,
    registerModule,
    isModuleVisible,
    promotion,
  }) =>
    renderSpecialPriceModule({
      content,
      isMobile,
      registerModule,
      isModuleVisible,
      promotion: promotion as PromotionInfo,
      promotionTitle: promotion?.title || '',
    }),
  FIRST_PURCHASE: ({
    content,
    isMobile,
    registerModule,
    isModuleVisible,
    promotion,
  }) =>
    renderFirstPurchaseModule({
      content,
      isMobile,
      registerModule,
      isModuleVisible,
      promotion: promotion as PromotionInfo,
    }),
  BRAND: ({ content, isMobile, registerModule, isModuleVisible, promotion }) =>
    renderBrandModule(
      content,
      isMobile,
      promotion?.title,
      registerModule,
      isModuleVisible
    ),
  RANKING: ({ content, isMobile, registerModule }) =>
    renderRankingModule(content, isMobile, registerModule),
  PAYMENT_BENEFIT: ({ content, isMobile, registerModule }) =>
    renderPaymentBenefitModule(content, isMobile, registerModule),
  SHORT_FORM: ({
    content,
    isMobile,
    registerModule,
    isModuleVisible,
    queryStatusMap,
    directQueryStatusMap,
    isPaused,
    setIsPaused,
    promotionTitle,
  }) =>
    renderShortFormModule({
      content,
      isMobile,
      registerModule,
      isModuleVisible,
      queryStatusMap,
      directQueryStatusMap,
      isPaused,
      setIsPaused,
      promotionTitle,
    }),
  CONTENT: ({ content, promotionTitle, isMobile, registerModule }) =>
    renderContentModule(content, isMobile, registerModule, promotionTitle),
  BASIC_NAVIGATION: ({
    content,
    isMobile,
    promotionContents,
    registerModule,
  }) =>
    renderBasicNavigationModule(
      content,
      isMobile,
      promotionContents ?? [],
      registerModule
    ),
  COMMON_NAVIGATION: ({ content, isMobile, promotionTitle, registerModule }) =>
    renderCommonNavigationModule(
      content,
      isMobile,
      promotionTitle || '',
      registerModule
    ),
  COUPON: ({ content, isMobile, registerModule }) =>
    renderCouponModule(content, isMobile, registerModule),
  FCFS_COUPON: ({
    content,
    isMobile,
    registerModule,
    promotionTitle,
    promotion,
  }) =>
    renderFcfsCouponModule(
      content,
      isMobile,
      registerModule,
      promotionTitle,
      promotion
    ),
  ADVERTISEMENT: ({ content, isMobile, promotion, registerModule }) =>
    renderAdvertisementModule(content, isMobile, promotion, registerModule),
  W_LIVE: ({ content, isMobile, registerModule }) =>
    renderWLiveModule(content, isMobile, registerModule),
  PRODUCT: ({
    content,
    isMobile,
    promotionTitle,
    registerModule,
    isModuleVisible,
  }) =>
    renderProductModule(
      content,
      isMobile,
      promotionTitle,
      registerModule,
      isModuleVisible
    ),
  PRODUCT_LIST: ({
    content,
    isMobile,
    registerModule,
    productListTotalCount,
  }) =>
    renderProductListModule(
      content,
      isMobile,
      registerModule,
      productListTotalCount
    ),
  HIGHLIGHT_PICK: ({ content, isMobile, registerModule, isModuleVisible }) =>
    renderHighlightPickModule({
      content,
      isMobile,
      registerModule,
      isModuleVisible,
    }),
  BRAND_COLLECTION: ({ content, isMobile, registerModule, gnbType }) =>
    renderBrandCollectionModule(content, isMobile, registerModule, gnbType),
  ENTRY: ({ content, isMobile, promotionTitle, registerModule }) =>
    renderEntryModule({
      content,
      isMobile,
      promotionTitle,
      registerModule,
    }),
  TOP_BUYER: ({ content, isMobile, registerModule, isLogin }) =>
    renderTopBuyerModule({ content, isMobile, registerModule, isLogin }),
};

export const renderMainBannerModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const filteredMainBannerModules = content.modules
    ?.filter(isMainBannerModule)
    .filter(
      module =>
        isInDisplayTimeRange(module.displayStartDate, module.displayEndDate) &&
        module.displayStatus === 'USE'
    );

  if (!filteredMainBannerModules || filteredMainBannerModules.length === 0) {
    return null;
  }

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <MainBannerModule
          modules={filteredMainBannerModules}
          isMobile={isMobile}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};

const QUICK_BANNER_MIN_DISPLAY_COUNT = 3;
const QUICK_BANNER_MAX_DISPLAY_COUNT = 10;

export const renderQuickBannerModule = (
  content: PromotionContent,
  isMobile: boolean,
  registerModule?: (contentId: string, element: HTMLElement | null) => void
): React.ReactNode => {
  if (content.displayStatus !== 'USE') return null;

  const filteredQuickBannerModules = content.modules
    ?.filter(isQuickBannerModule)
    .filter(
      module =>
        isInDisplayTimeRange(module.displayStartDate, module.displayEndDate) &&
        module.displayStatus === 'USE'
    )
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .slice(0, QUICK_BANNER_MAX_DISPLAY_COUNT);

  if (
    !filteredQuickBannerModules ||
    filteredQuickBannerModules.length < QUICK_BANNER_MIN_DISPLAY_COUNT
  ) {
    return null;
  }

  return (
    <ModuleErrorBoundary key={content.contentId}>
      <ModuleAnchorWrapper
        order={content.displayOrder}
        id={`section${content.displayOrder}`}
        key={content.contentId}
        registerModule={registerModule}
        contentId={content.contentId.toString()}
        isDesktop={!isMobile}
      >
        <QuickBannerModule
          modules={filteredQuickBannerModules}
          isMobile={isMobile}
          contentTitle={content.mainTitle}
        />
      </ModuleAnchorWrapper>
    </ModuleErrorBoundary>
  );
};
